import type { PipelineItem } from '@/features/adp/api/pipelineLists.api'
import {
  downloadDQReport,
  downloadDestinationFile,
} from '@/features/adp/api/pipelineListsdqdownload.api'
import type { JobMonitoringMessage, JobMonitoringStatus } from '@/features/adp/hooks/useJobMonitoringWebSocket'
import { usePipelineLists } from '@/features/adp/hooks/usePipelineLists'
import { openOverlay } from '@/features/adp/state/overlaySlice'
import ActionHeader from '@/shared/components/ActionHeader/ActionHeader'
import Pagination from '@/shared/components/Pagination/Pagination'
import { Button } from '@/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/ui/dropdown-menu'
import { MaterialIcon } from '@/ui/material-icon'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/ui/tooltip'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'sonner'
import styles from './PipelinesListView.module.scss'
import { cn } from '@/shared/lib/utils'

export type PipelinesListViewProps = {
  isActive?: boolean
  onNewPipeline: () => void
  onOpenPipeline: (pipelineId: string) => void
  onQualityReport?: (pipelineId: string) => void
  onRunImmediate?: (sessionId: string) => Promise<void> | void
  onStopPipeline?: (sessionId: string) => Promise<void> | void
  /** Live status overrides from WebSocket keyed by data_flow_id */
  liveStatuses?: Record<number, JobMonitoringStatus>
  /** Live full message payloads from WebSocket keyed by data_flow_id */
  liveMessages?: Record<number, JobMonitoringMessage>
}

type PipelineStatus =
  | 'finished'
  | 'stopped'
  | 'initializing'
  | 'in_progress'
  | 'failed'
  | 'not_started'

type PipelineRow = {
  id: string
  dataFlowId: number
  name: string
  type: string
  sessionId: string
  createdAt: string
  lastExecutedAt: string
  status: PipelineStatus
  isDownloadable: boolean
  hasQualityReport: boolean
  passedRecords?: number
  failedRecords?: number
  downloadFolderName?: string
  downloadTargetTableId?: number | null
  hasPipelineDefinition: boolean
  isSchedule: boolean
  scheduleTypeName: string | null
  scheduleDetails?: PipelineItem['schedule_details']
}

export type PipelineScheduleTarget = Pick<
  PipelineRow,
  'id' | 'name' | 'sessionId' | 'isSchedule' | 'scheduleTypeName' | 'scheduleDetails'
>

const PAGE_SIZE_OPTIONS = ['10', '25', '50'] as const
const MAX_FREQUENCY_LABEL_LENGTH = 15

function mapApiStatusToUiStatus(apiStatus: string): PipelineStatus {
  const status = (apiStatus || '').toLowerCase().trim()
  switch (status) {
    case 'running': return 'in_progress'
    case 'finished': return 'finished'
    case 'stopped': return 'stopped'
    case 'initializing': return 'initializing'
    case 'failed': return 'failed'
    case '': return 'not_started'
    default: return 'not_started'
  }
}

/** Maps live WebSocket JobMonitoringStatus → PipelineStatus for the list */
function mapJobStatusToPipelineStatus(js: JobMonitoringStatus): PipelineStatus {
  switch (js) {
    case 'initializing': return 'initializing'
    case 'connecting': return 'initializing'
    case 'running': return 'in_progress'
    case 'finished': return 'finished'
    case 'failed': return 'failed'
    case 'stopped': return 'stopped'
    default: return 'not_started'
  }
}

function formatDate(isoString: string): string {
  const date = new Date(isoString)
  return (
    date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }) +
    ', ' +
    date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  )
}

function truncateFrequencyLabel(value: string): string {
  if (value.length <= MAX_FREQUENCY_LABEL_LENGTH) return value
  return `${value.slice(0, MAX_FREQUENCY_LABEL_LENGTH)}...`
}

function hasPipelineDefinitionValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

function extractGrandTotal(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    const grandTotal = record.Grand_Total
    if (typeof grandTotal === 'number') return Number.isFinite(grandTotal) ? grandTotal : undefined
    if (typeof grandTotal === 'string' && grandTotal.trim() !== '') {
      const parsed = Number(grandTotal)
      return Number.isFinite(parsed) ? parsed : undefined
    }
  }
  return undefined;
}

function StatusBadge({ status }: Readonly<{ status: PipelineStatus }>) {
  switch (status) {
    case 'finished':
      return (
        <span className={`${styles.statusPill} ${styles.statusFinished}`}>
          <MaterialIcon name="check" size={14} />
          Finished
        </span>
      )
    case 'stopped':
      return (
        <span className={`${styles.statusPill} ${styles.statusStopped}`}>
          <MaterialIcon name="stop" size={14} />
          Stopped
        </span>
      )
    case 'initializing':
      return (
        <span className={`${styles.statusPill} ${styles.statusInitializing}`}>
          <MaterialIcon name="schedule" size={14} />
          Initializing
        </span>
      )
    case 'in_progress':
      return (
        <span className={`${styles.statusPill} ${styles.statusInProgress}`}>
          <MaterialIcon name="sync" size={14} />
          In Progress
        </span>
      )
    case 'failed':
      return (
        <span className={`${styles.statusPill} ${styles.statusFailed}`}>
          <MaterialIcon name="error" size={14} />
          Failed
        </span>
      )
    case 'not_started':
    default:
      return (
        <span className={`${styles.statusPill} ${styles.statusNotStarted}`}>
          <MaterialIcon name="radio_button_unchecked" size={14} />
          Not Started
        </span>
      )
  }
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'running', label: 'Running' },
  { value: 'Initializing', label: 'Initializing' },
  { value: 'finished', label: 'Finished' },
  { value: 'failed', label: 'Failed' },
]

function DataQualityReportModal({
  open,
  pipelineId,
  pipelineName,
  passedRecords,
  failedRecords,
  onClose,
}: Readonly<{
  open: boolean
  pipelineId: string
  pipelineName: string
  passedRecords?: number
  failedRecords?: number
  onClose: () => void
}>) {
  const [isDownloading, setIsDownloading] = useState(false)

  const passed = passedRecords ?? 0;
  const failed = failedRecords ?? 0;

  const handleDownload = async () => {
    setIsDownloading(true)
    try {
      await downloadDQReport(pipelineId)
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Download failed')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent className={styles.dialogContent}>
        <DialogHeader className={styles.dialogHeader}>
          <DialogTitle className={styles.modalTitle}>
            Data Quality Report — {pipelineName}
          </DialogTitle>
        </DialogHeader>

        <div className={styles.modalBody}>
          <div className={styles.qualityStats}>
            <div className={styles.qualityStatCard}>
              <span className={styles.qualityStatValue}>{passed}</span>
              <span className={styles.qualityStatLabel}>Passed</span>
            </div>
            <div className={styles.qualityStatCard}>
              <span className={styles.qualityStatValue}>{failed}</span>
              <span className={styles.qualityStatLabel}>Failed</span>
            </div>
          </div>

          <div className={styles.modalFooter}>
            <button
              type="button"
              className={styles.modalDownloadBtn}
              onClick={handleDownload}
              disabled={isDownloading}
            >
              <MaterialIcon name="download" size={18} />
              {isDownloading ? 'Downloading...' : 'Download Report'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

type ActionState = {
  iconName: string
  actionLabel: string
  ariaLabel: string
}

function getActionState(status: PipelineStatus, pipelineName: string): ActionState {
  if (status === 'initializing' || status === 'in_progress') {
    return {
      iconName: 'stop_circle',
      actionLabel: 'Stop',
      ariaLabel: `Stop ${pipelineName}`,
    }
  }

  if (status === 'finished' || status === 'failed') {
    return {
      iconName: 'refresh',
      actionLabel: 'Re-Run',
      ariaLabel: `Re-run ${pipelineName}`,
    }
  }

  return {
    iconName: 'play_arrow',
    actionLabel: 'Run',
    ariaLabel: `Run ${pipelineName}`,
  }
}

// ── Row Actions (shadcn DropdownMenu) ────────────────────────────────────────
function StatusBasedActionButton({
  status,
  sessionId,
  isLoading: externalIsLoading,
  onRunImmediate,
  onStopPipeline,
  pipelineName,
  onLoadingChange,
}: Readonly<{
  status: PipelineStatus
  sessionId: string
  isLoading: boolean
  onRunImmediate?: (sessionId: string) => Promise<void> | void
  onStopPipeline?: (sessionId: string) => Promise<void> | void
  pipelineName: string
  onLoadingChange?: (isLoading: boolean) => void
}>) {
  const [internalLoading, setInternalLoading] = useState(false)
  const isActionDisabled = externalIsLoading || internalLoading

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    if (!sessionId) return
    setInternalLoading(true)
    onLoadingChange?.(true)
    try {
      if (status === 'initializing' || status === 'in_progress') {
        await onStopPipeline?.(sessionId)
      } else {
        await onRunImmediate?.(sessionId)
      }
    } catch (err) {
      console.error('Action failed:', err)
    } finally {
      setInternalLoading(false)
      onLoadingChange?.(false)
    }
  }

  const { iconName, actionLabel, ariaLabel } = getActionState(status, pipelineName);

  return (
    <button
      type="button"
      className={cn(styles.iconAction, styles.iconActionWithLabel)}
      aria-label={ariaLabel}
      disabled={isActionDisabled}
      onClick={handleClick}
      title={actionLabel}
    >
      <MaterialIcon name={iconName} size={20} />
      <span className={styles.actionLabel}>{actionLabel}</span>
    </button>
  )
}

function RowActionsMenu({
  pipeline,
  isDownloadable,
  hasQualityReport,
  downloadFolderName,
  downloadTargetTableId,
  onQualityReport,
}: Readonly<{
  pipeline: PipelineScheduleTarget
  isDownloadable: boolean
  hasQualityReport: boolean
  downloadFolderName?: string
  downloadTargetTableId?: number | null
  onQualityReport?: (id: string) => void
}>) {
  const dispatch = useDispatch()
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (!downloadFolderName || downloadTargetTableId == null) {
      alert('Download is not available for this pipeline.')
      return
    }
    setIsDownloading(true)
    try {
      await downloadDestinationFile(downloadFolderName, downloadTargetTableId)
    } catch (err) {
      console.error('Download failed:', err)
      alert(err instanceof Error ? err.message : 'Download failed')
    } finally {
      setIsDownloading(false)
    }
  }

  const handleDeletePipeline = () => {
    dispatch(
      openOverlay({
        type: 'delete-pipeline-dialog',
        data: {
          pipelineId: pipeline.id,
          pipelineName: pipeline.name,
          source: 'pipeline-list',
        },
      })
    )
  }

  const handleScheduleOverlayOpen = () => {
    dispatch(
      openOverlay({
        type: 'schedule-pipeline-modal',
        data: {
          pipelineName: pipeline.name,
          sessionId: pipeline.sessionId,
          isSchedule: pipeline.isSchedule,
          scheduleDetails: pipeline.scheduleDetails,
          scheduleTypeName: pipeline.scheduleTypeName ?? null,
        },
      })
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={styles.iconAction}
          aria-label="More actions"
        >
          <MaterialIcon name="more_vert" size={20} />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className={styles.dropdownMenu}>
        {hasQualityReport && (
          <DropdownMenuItem
            className={styles.dropdownItem}
            onSelect={() => onQualityReport?.(pipeline.id)}
          >
            <MaterialIcon name="assessment" size={18} />
            <span>Quality Report</span>
          </DropdownMenuItem>
        )}

        {isDownloadable && (
          <DropdownMenuItem
            className={styles.dropdownItem}
            onSelect={handleDownload}
            disabled={isDownloading}
          >
            <MaterialIcon name="download" size={18} />
            <span>{isDownloading ? 'Downloading...' : 'Download'}</span>
          </DropdownMenuItem>
        )}

        {pipeline.scheduleTypeName ? (
          <DropdownMenuItem
            className={styles.dropdownItem}
            onSelect={handleScheduleOverlayOpen}
          >
            <MaterialIcon name="edit" size={18} />
            <span>Edit Pipeline Schedule</span>
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            className={styles.dropdownItem}
            onSelect={handleScheduleOverlayOpen}
          >
            <MaterialIcon name="schedule" size={18} />
            <span>Create Pipeline Schedule</span>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator className={styles.separator} />

        <DropdownMenuItem
          className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
          onSelect={() => handleDeletePipeline()}
        >
          <MaterialIcon name="delete" size={18} />
          <span>Delete</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ── Main view ────────────────────────────────────────────────────────────────
export function PipelinesListView({
  isActive = false,
  onNewPipeline,
  onOpenPipeline,
  onQualityReport,
  onRunImmediate,
  onStopPipeline,
  liveStatuses = {},
  liveMessages = {},
}: Readonly<PipelinesListViewProps>) {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>('10')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const [spinningRows, setSpinningRows] = useState<Record<string, boolean>>({})

  const [qualityReportPipeline, setQualityReportPipeline] = useState<{
    id: string
    name: string
    passedRecords?: number
    failedRecords?: number
  } | null>(null)

  const size = Number.parseInt(pageSize, 10)
  const apiStatusParam = statusFilter === 'all' ? undefined : statusFilter

  const { pipelines, totalCount, isLoading, error } = usePipelineLists(
    {
      page,
      size,
      search: search.trim() || undefined,
      status: apiStatusParam,
    },
    { enabled: isActive }
  )

  const totalPages = Math.max(1, Math.ceil(totalCount / size))

  useEffect(() => {
    setPage((p) => Math.min(p, totalPages))
  }, [totalPages])

  const displayPage = Math.min(page, totalPages)

  const pipelineRows: PipelineRow[] = useMemo(
    () =>
      pipelines.map((item) => {
        const baseStatus = mapApiStatusToUiStatus(item.status)
        const liveJobStatus = liveStatuses[item.data_flow_id]
        const liveMsg = liveMessages[item.data_flow_id]

        const resolvedStatus = liveJobStatus
          ? mapJobStatusToPipelineStatus(liveJobStatus)
          : baseStatus

        // Live overrides from WebSocket
        const lastExecutedRaw = liveMsg?.last_executed ?? item.last_modified_datetime_timestamp
        const dqPassedRaw = liveMsg?.total_dq_passed_records ?? item.total_dq_passed_records
        const dqFailedRaw = liveMsg?.total_dq_failed_records ?? item.total_dq_failed_records
        const hasQualityReport =
          !(liveMsg?.total_dq_passed_records == null && liveMsg?.total_dq_failed_records == null) ||
          !(item.total_dq_passed_records == null && item.total_dq_failed_records == null)

        const parsedPassed = extractGrandTotal(dqPassedRaw)
        const parsedFailed = extractGrandTotal(dqFailedRaw)

        return {
          id: String(item.data_flow_id),
          dataFlowId: item.data_flow_id,
          name: item.data_flow_name,
          type: item.data_flow_type,
          sessionId: item.session_id,
          createdAt: formatDate(item.created_datetime_timestamp),
          lastExecutedAt: formatDate(lastExecutedRaw),
          status: resolvedStatus,
          isDownloadable: Boolean(item.is_downloadable),
          hasQualityReport,
          passedRecords: parsedPassed,
          failedRecords: parsedFailed,
          downloadFolderName: item.folder_name ?? undefined,
          downloadTargetTableId: item.target_table_id,
          hasPipelineDefinition: hasPipelineDefinitionValue(item.pipeline_definition),
          isSchedule: Boolean(item.is_schedule),
          scheduleTypeName: item.schedule_type_name ?? null,
          scheduleDetails: item.schedule_details ?? null,
        }
      }),
    [pipelines, liveStatuses, liveMessages]
  )

  const handleStatusSelect = (value: string) => {
    setStatusFilter(value)
    setPage(1)
  }

  const handleQualityReport = (pipelineId: string) => {
    const row = pipelineRows.find((r) => r.id === pipelineId)
    setQualityReportPipeline({
      id: pipelineId,
      name: row?.name ?? '',
      passedRecords: row?.passedRecords,
      failedRecords: row?.failedRecords,
    })
    onQualityReport?.(pipelineId)
  }

  const handleRefreshPipelines = () => {
    void queryClient.invalidateQueries({ queryKey: ['adp', 'pipeline-lists'] })
  }

  let tableBodyContent: React.ReactNode
  if (isLoading) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={7} className={styles.loadingCell}>
          Loading pipelines...
        </TableCell>
      </TableRow>
    )
  } else if (error) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={7} className={styles.errorCell}>
          Error loading pipelines: {error}
        </TableCell>
      </TableRow>
    )
  } else if (pipelineRows.length === 0) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={7} className={styles.emptyCell}>
          No pipelines found.
        </TableCell>
      </TableRow>
    )
  } else {
    tableBodyContent = pipelineRows.map((row, idx) => {
      const canViewPipeline = row.hasPipelineDefinition
      return (
        <TableRow
          key={row.id}
          className={idx % 2 === 1 ? styles.rowAlt : undefined}
        >
          <TableCell className={styles.cellStrong}>{row.name}</TableCell>
          <TableCell className={styles.cellMuted}>{row.createdAt}</TableCell>
          <TableCell className={styles.cellMuted}>{row.lastExecutedAt}</TableCell>
          <TableCell className={styles.statusCell}>
            <StatusBadge status={row.status} />
          </TableCell>
          <TableCell className={styles.cellMuted}>
            {row.isSchedule ? 'Scheduled' : 'Immediate'}
          </TableCell>
          <TableCell className={styles.cellMuted}>
            {row.scheduleTypeName && row.scheduleTypeName.length > MAX_FREQUENCY_LABEL_LENGTH ? (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className={styles.frequencyValue}>
                      {truncateFrequencyLabel(row.scheduleTypeName)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {row.scheduleTypeName}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <span className={styles.frequencyValue}>{row.scheduleTypeName ?? '-'}</span>
            )}
          </TableCell>
          <TableCell>
            <div className={styles.actionsCell}>
              <StatusBasedActionButton
                status={row.status}
                sessionId={row.sessionId}
                isLoading={spinningRows[row.id] || false}
                onRunImmediate={onRunImmediate}
                onStopPipeline={onStopPipeline}
                pipelineName={row.name}
                onLoadingChange={(loading) => {
                  setSpinningRows(prev => ({ ...prev, [row.id]: loading }))
                }}
              />
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <button
                        type="button"
                        className={`${styles.iconAction} ${styles.iconActionWithLabel} ${canViewPipeline ? '' : styles.iconActionDisabled}`}
                        aria-label={
                          canViewPipeline
                            ? `View ${row.name}`
                            : `View disabled for ${row.name}`
                        }
                        onClick={() => {
                          if (!canViewPipeline) return
                          onOpenPipeline(row.id)
                        }}
                        disabled={!canViewPipeline}
                      >
                        <MaterialIcon name="visibility" size={20} />
                        <span className={styles.actionLabel}>View</span>
                      </button>
                    </span>
                  </TooltipTrigger>
                  {!canViewPipeline && (
                    <TooltipContent side="top">
                      This pipeline does not support viewing yet.
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
              <RowActionsMenu
                pipeline={row}
                isDownloadable={row.isDownloadable}
                hasQualityReport={row.hasQualityReport}
                downloadFolderName={row.downloadFolderName}
                downloadTargetTableId={row.downloadTargetTableId}
                onQualityReport={handleQualityReport}
              />
            </div>
          </TableCell>
        </TableRow>
      )
    })
  }

  return (
    <div className={styles.root}>
      <div className={styles.inner}>
        {/* ── Header ── */}
        <header className={styles.pageHeader}>
          <ActionHeader
            className={styles.pipelinesActionHeader}
            title="Pipelines List"
            leadingActions={[
              {
                icon: 'cached',
                variant: 'icon',
                onClick: handleRefreshPipelines,
                ariaLabel: 'refresh-pipelines',
                key: 'refresh-pipelines',
              },
            ]}
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search Pipeline"
            searchInputClassName={styles.pipelinesSearchInput}
            actions={[
              {
                label: 'New Pipeline',
                icon: 'add',
                onClick: onNewPipeline,
                ariaLabel: 'new-pipeline',
                key: 'new-pipeline',
              },
            ]}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Filter pipelines"
                  className={styles.filterIconBtn}
                >
                  <MaterialIcon name="filter_list" size={18} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className={styles.filterDropdownMenu}>
                <div className={styles.filterDropdownHeader}>
                  <MaterialIcon name="filter_list" size={16} />
                  <span>Filter by Status</span>
                </div>
                {STATUS_OPTIONS.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    className={`${styles.filterDropdownItem} ${statusFilter === option.value ? styles.filterDropdownItemActive : ''}`}
                    onSelect={() => handleStatusSelect(option.value)}
                  >
                    <span>{option.label}</span>
                    {statusFilter === option.value && (
                      <MaterialIcon name="check" size={16} />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </ActionHeader>
        </header>

        {/* ── Table ── */}
        <div className={styles.tableScroll}>
          <Table className={styles.dataTable}>
            <TableHeader>
              <TableRow className={styles.headerRow}>
                <TableHead className={styles.colName}>Name</TableHead>
                <TableHead className={styles.colDate}>Created Date</TableHead>
                <TableHead className={styles.colDate}>Last Executed Date</TableHead>
                <TableHead className={styles.colStatus}>Status</TableHead>
                <TableHead className={styles.colRunTime}>Run Type</TableHead>
                <TableHead className={styles.colFrequency}>Frequency</TableHead>
                <TableHead className={styles.colActions}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tableBodyContent}
            </TableBody>
          </Table>
        </div>

        {/* ── Footer / Pagination ── */}
        <div className={styles.footer}>
          <div className={styles.footerLeft} />
          <div className={styles.footerRight}>
            <Pagination
              currentPage={displayPage}
              totalPages={totalPages}
              rowsPerPage={size}
              onPageChange={setPage}
              onRowsPerPageChange={(n) => {
                setPageSize(String(n) as (typeof PAGE_SIZE_OPTIONS)[number])
                setPage(1)
              }}
              pageSizeOptions={[10, 25, 50]}
            />
          </div>
        </div>
      </div>

      {/* ── Quality Report Dialog ── */}
      {qualityReportPipeline && (
        <DataQualityReportModal
          open={!!qualityReportPipeline}
          pipelineId={qualityReportPipeline.id}
          pipelineName={qualityReportPipeline.name}
          passedRecords={qualityReportPipeline.passedRecords}
          failedRecords={qualityReportPipeline.failedRecords}
          onClose={() => setQualityReportPipeline(null)}
        />
      )}
    </div>
  )
}
